import { ref, type Ref } from 'vue'
import { useRouter } from 'vue-router'
import { apiFetch } from '../../services/api'

/**
 * Shared action handlers for the sales play.
 *
 * Usage:
 *   const actions = useSalesPlayActions(playId, play, refreshPlay)
 *
 * `refreshPlay` is a callback the page provides so the composable can
 * tell the page to re-fetch play data after a mutation.
 */
export function useSalesPlayActions(
  playId: Ref<string>,
  play: Ref<any>,
  refreshPlay: () => Promise<void>,
) {
  const router = useRouter()

  // ── Pause / Resume ──────────────────────────────────────────────

  function onTogglePause() {
    if (!play.value || play.value.status === 'archived') return
    const nextStatus = play.value.status === 'active' ? 'paused' : 'active'
    void apiFetch(`/sales-plays/${playId.value}`, {
      method: 'PATCH',
      body: { update: { config: { status: nextStatus } } },
    }).then(() => refreshPlay()).catch((e) => {
      console.error('[sales-play] pause/resume failed', e)
    })
  }

  // ── Delete ──────────────────────────────────────────────────────

  const deleteBusy = ref(false)
  const deleteConfirm = ref<{
    active_accounts: number
    active_contacts: number
    live_enrollment_count: number
    pending_reachout_count: number
    dialer_entry_count: number
  } | null>(null)

  async function onDeleteClick() {
    if (!play.value || deleteBusy.value) return
    try {
      const preview: any = await apiFetch(`/sales-plays/${playId.value}/delete-preview`)
      deleteConfirm.value = preview
    } catch { /* preview failed — silently ignore */ }
  }

  async function onDeleteConfirm() {
    if (!play.value || deleteBusy.value) return
    deleteBusy.value = true
    try {
      await apiFetch(`/sales-plays/${playId.value}`, { method: 'DELETE' })
      deleteConfirm.value = null
      router.push('/plays')
    } catch { /* surfaces in next poll */ }
    finally { deleteBusy.value = false }
  }

  // ── Export ──────────────────────────────────────────────────────

  async function onExport() {
    if (!play.value) return
    try {
      const blob = await apiFetch(`/sales-plays/${playId.value}/export`, { responseType: 'blob' })
      const slug = String(play.value.name || '').trim().toLowerCase()
        .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
      const filename = `${slug || playId.value}.json`
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (e: any) {
      alert(e?.message || 'Export failed')
    }
  }

  // ── Edit name & description ────────────────────────────────────

  const editDetailsOpen = ref(false)
  const editDetailsBusy = ref(false)
  const editDetailsError = ref('')
  const editDetailsDraft = ref({ name: '', description: '' })

  function openEditDetails() {
    if (!play.value) return
    editDetailsDraft.value = {
      name: play.value.name || '',
      description: play.value.description || '',
    }
    editDetailsError.value = ''
    editDetailsOpen.value = true
  }

  async function saveEditDetails() {
    if (!play.value || !editDetailsDraft.value.name.trim()) return
    editDetailsBusy.value = true
    editDetailsError.value = ''
    try {
      await apiFetch(`/sales-plays/${playId.value}`, {
        method: 'PATCH',
        body: {
          update: {
            config: {
              name: editDetailsDraft.value.name.trim(),
              description: editDetailsDraft.value.description.trim() || null,
            },
          },
        },
      })
      editDetailsOpen.value = false
      await refreshPlay()
    } catch (e: any) {
      editDetailsError.value = e?.message || 'Could not save changes'
    } finally {
      editDetailsBusy.value = false
    }
  }

  return {
    // Pause
    onTogglePause,
    // Delete
    deleteBusy,
    deleteConfirm,
    onDeleteClick,
    onDeleteConfirm,
    // Export
    onExport,
    // Edit details
    editDetailsOpen,
    editDetailsBusy,
    editDetailsError,
    editDetailsDraft,
    openEditDetails,
    saveEditDetails,
  }
}
